import type { AuthResponse } from '../xp';

type Prefix = '' | '!' | '>=';

/**
 * A map of permission role keys to the location numbers that hold that role.
 * Build one from an {@link AuthResponse} using {@link toPermissionsMap}.
 */
export type PermissionsMap = Record<string, string[]>;

// --- Expression tree --------------------------------------------------------

type Expr =
  | { type: 'leaf'; prefix: Prefix; key: LevelKey }
  | { type: 'and'; left: Expr; right: Expr }
  | { type: 'or'; left: Expr; right: Expr };

// --- Public types -----------------------------------------------------------

interface BaseExpr {
  readonly _expr: Expr;
}

/**
 * A single permission check — the starting point before any combinator.
 * Exposes both `.and` and `.or` for the first combination step.
 */
interface LevelExpr extends BaseExpr {
  readonly and: CombinatorContext<AndExpr>;
  readonly or: CombinatorContext<OrExpr>;
}

/**
 * Result of an `.and` combination. Only `.and` is available for further
 * chaining — mixing `.or` after `.and` is a compile-time error.
 */
interface AndExpr extends BaseExpr {
  readonly and: CombinatorContext<AndExpr>;
}

/**
 * Result of an `.or` combination. Only `.or` is available for further
 * chaining — mixing `.and` after `.or` is a compile-time error.
 */
interface OrExpr extends BaseExpr {
  readonly or: CombinatorContext<OrExpr>;
}

interface CombinatorContext<R extends BaseExpr> extends LevelMapOf<R> {
  readonly not: LevelMapOf<R>;
  readonly atLeast: LevelMapOf<R>;
}

/** JSDoc on each key transfers to mapped types via `keyof LevelNames`. */
interface LevelNames {
  /** Logged-in user with basic access at one or more locations. */
  LOGIN: unknown;
  /** User with Trainer permission — set in TMUA. */
  TRAINER: unknown;
  /** User with Leader permission — set in TMUA. */
  LEADER: unknown;
  /** User with Operator permission — set in ServiceGateway. */
  OPERATOR: unknown;
  /** User with Stakeholder permission — all staff have this by default. */
  STAKEHOLDER: unknown;
  /** User with Admin permission — highest privilege. */
  ADMIN: unknown;
  /** Leader **or** Operator at any location. */
  LO: unknown;
  /** Trainer, Leader, **or** Operator at any location. */
  TLO: unknown;
}

type LevelMapOf<R> = { readonly [K in keyof LevelNames]: R };

/**
 * Any valid permission expression accepted by {@link checkUserPermissionLevel}.
 * Produced by {@link PermissionLevel} — simple levels, negation (`not`),
 * minimum-threshold (`atLeast`), and fluent `.and` / `.or` combinations.
 */
export type PermissionExpr = LevelExpr | AndExpr | OrExpr;

// --- Level keys & config ----------------------------------------------------

type LevelKey = keyof LevelNames;
type CompositeKey = Extract<LevelKey, 'LO' | 'TLO'>;
type RoleKey = Exclude<LevelKey, CompositeKey>;

/**
 * Ascending role hierarchy.
 * @see {@link https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/3781493005 Pathway WAS Application Permissions}
 */
const roleHierarchy: readonly RoleKey[] = [
  'LOGIN',
  'TRAINER',
  'LEADER',
  'OPERATOR',
  'STAKEHOLDER',
  'ADMIN',
];

const compositeConfig = {
  LO: { roles: ['LEADER', 'OPERATOR'] as const },
  TLO: { roles: ['TRAINER', 'LEADER', 'OPERATOR'] as const },
} as const satisfies Record<CompositeKey, { roles: readonly RoleKey[] }>;

const levelKeys = [
  ...roleHierarchy,
  ...Object.keys(compositeConfig),
] as LevelKey[];

// --- Permissions extraction -------------------------------------------------

/**
 * Extracts a {@link PermissionsMap} from an {@link AuthResponse}, keyed by
 * permission role with arrays of location numbers that hold that role.
 */
export const toPermissionsMap = (auth: AuthResponse): PermissionsMap =>
  (auth.user?.locations ?? []).reduce<PermissionsMap>(
    // xp still populates roles; permissions do not replace this map yet.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    (acc, { location, roles }) => {
      const number = location.number;
      if (!number) return acc;
      roles.forEach(p => {
        (acc[p] ??= []).push(number);
      });
      return acc;
    },
    {},
  );

// --- Leaf checkers ----------------------------------------------------------

const hasRole = (perms: PermissionsMap, role: RoleKey) =>
  (perms[role]?.length ?? 0) > 0;

const permissionCheckers = {
  ...Object.fromEntries(
    roleHierarchy.map(role => [
      role,
      (perms: PermissionsMap) => hasRole(perms, role),
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(compositeConfig).map(([key, { roles }]) => [
      key,
      (perms: PermissionsMap) => roles.some(r => hasRole(perms, r)),
    ]),
  ),
} as Record<LevelKey, (perms: PermissionsMap) => boolean>;

const roleAtLeastChecker = (minIdx: number) => (perms: PermissionsMap) =>
  roleHierarchy.slice(minIdx).some(r => hasRole(perms, r));

const atLeastCheckers = {
  ...Object.fromEntries(
    roleHierarchy.map((role, idx) => [role, roleAtLeastChecker(idx)]),
  ),
  ...Object.fromEntries(
    Object.entries(compositeConfig).map(([key, { roles }]) => {
      const minIdx = Math.min(...roles.map(r => roleHierarchy.indexOf(r)));
      return [key, roleAtLeastChecker(minIdx)];
    }),
  ),
} as Record<LevelKey, (perms: PermissionsMap) => boolean>;

// --- Expression builders ----------------------------------------------------

const defineGetter = <T>(obj: T, key: string, get: () => unknown) =>
  Object.defineProperty(obj, key, { get, configurable: true });

const makeAndExpr = (expr: Expr): AndExpr => {
  const obj = { _expr: expr } as AndExpr;
  defineGetter(obj, 'and', () => makeCombinator(expr, 'and', makeAndExpr));
  return obj;
};

const makeOrExpr = (expr: Expr): OrExpr => {
  const obj = { _expr: expr } as OrExpr;
  defineGetter(obj, 'or', () => makeCombinator(expr, 'or', makeOrExpr));
  return obj;
};

const makeLevelExpr = (expr: Expr): LevelExpr => {
  const obj = { _expr: expr } as LevelExpr;
  defineGetter(obj, 'and', () => makeCombinator(expr, 'and', makeAndExpr));
  defineGetter(obj, 'or', () => makeCombinator(expr, 'or', makeOrExpr));
  return obj;
};

const makeCombinator = <R extends BaseExpr>(
  left: Expr,
  op: 'and' | 'or',
  wrap: (expr: Expr) => R,
): CombinatorContext<R> => {
  const build = (prefix: Prefix) =>
    Object.fromEntries(
      levelKeys.map(k => [
        k,
        wrap({
          type: op,
          left,
          right: { type: 'leaf', prefix, key: k },
        }),
      ]),
    ) as unknown as LevelMapOf<R>;

  const ctx = { ...build('') } as CombinatorContext<R>;
  defineGetter(ctx, 'not', () => build('!'));
  defineGetter(ctx, 'atLeast', () => build('>='));
  return ctx;
};

const buildLevelMap = (prefix: Prefix) =>
  Object.fromEntries(
    levelKeys.map(k => [k, makeLevelExpr({ type: 'leaf', prefix, key: k })]),
  ) as unknown as LevelMapOf<LevelExpr>;

// --- PermissionLevel root ---------------------------------------------------

interface PermissionLevelRoot extends LevelMapOf<LevelExpr> {
  /** The user does **not** have the permission. */
  readonly not: LevelMapOf<LevelExpr>;
  /** The user has **at least** the permission. */
  readonly atLeast: LevelMapOf<LevelExpr>;
}

/**
 * Permission level tokens for use with {@link checkUserPermissionLevel}.
 *
 * Pathway roles (ascending): LOGIN, TRAINER, LEADER, OPERATOR, STAKEHOLDER, ADMIN.
 * Composite levels: LO (Leader or Operator), TLO (Trainer, Leader, or Operator).
 * Permissions are set in TMUA (Trainer/Leader) or ServiceGateway (Staff).
 * All staff have LOGIN and STAKEHOLDER by default.
 *
 * Supports fluent `.and` / `.or` combinators for logical combinations.
 * Chains are homogeneous — after `.and`, only `.and` is available (and vice
 * versa). This prevents mixed-operator ambiguity at compile time.
 *
 * @see {@link https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/3781493005 Pathway WAS Application Permissions}
 *
 * @example
 * const perms = toPermissionsMap(authResponse);
 * checkUserPermissionLevel(perms, PermissionLevel.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.not.TRAINER);
 * checkUserPermissionLevel(perms, PermissionLevel.atLeast.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.LO);
 * checkUserPermissionLevel(perms, PermissionLevel.ADMIN.and.not.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.LEADER.or.OPERATOR);
 */
export const PermissionLevel: PermissionLevelRoot = {
  ...buildLevelMap(''),
  not: buildLevelMap('!'),
  atLeast: buildLevelMap('>='),
};

// --- Expression evaluator ---------------------------------------------------

const evalExpr = (expr: Expr, perms: PermissionsMap): boolean => {
  switch (expr.type) {
    case 'leaf':
      if (expr.prefix === '!') return !permissionCheckers[expr.key](perms);
      if (expr.prefix === '>=') return atLeastCheckers[expr.key](perms);
      return permissionCheckers[expr.key](perms);
    case 'and':
      return evalExpr(expr.left, perms) && evalExpr(expr.right, perms);
    case 'or':
      return evalExpr(expr.left, perms) || evalExpr(expr.right, perms);
  }
};

/**
 * Check whether the current user holds a given permission level.
 *
 * Accepts any expression from {@link PermissionLevel}: simple levels,
 * negation (`not`), minimum-threshold (`atLeast`), and fluent
 * `.and` / `.or` combinations.
 *
 * Build the {@link PermissionsMap} first with {@link toPermissionsMap}.
 *
 * @see {@link https://cfacorp.atlassian.net/wiki/spaces/TEAM/pages/3781493005 Pathway WAS Application Permissions}
 *
 * @example
 * const perms = toPermissionsMap(authResponse);
 * checkUserPermissionLevel(perms, PermissionLevel.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.not.TRAINER);
 * checkUserPermissionLevel(perms, PermissionLevel.atLeast.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.LO);
 * checkUserPermissionLevel(perms, PermissionLevel.ADMIN.and.not.LEADER);
 * checkUserPermissionLevel(perms, PermissionLevel.LEADER.or.OPERATOR);
 */
export const checkUserPermissionLevel = (
  perms: PermissionsMap,
  level: PermissionExpr,
): boolean => evalExpr(level._expr, perms);
